import {DocumentThread, EditorDoc, EditorDocStatus, EditorDocTemplateType} from 'client';
import {DocumentStatus, TopNavDocument} from 'components/DSYS/document/types';
import {getDocStatus} from 'components/DSYS/document/utils';
import {ROUTE_PATHS} from 'constant';
import {formatShortDate} from 'helpers/dates';
import {createSlug} from 'helpers/utils';
import {t} from 'i18next';
import {atomWithStorage} from 'jotai/utils';
import {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';

export const recentPlanningDocsAtom = atomWithStorage(
  'recentPlanningDocs',
  [] as {
    id: string;
    pinned: boolean;
  }[]
);

export const DEFAULT_NARRATIVE_STATUS_OPTIONS = [DocumentStatus.DRAFT, DocumentStatus.PUBLISHED];
export const DEFAULT_SWITCHBOARD_STATUS_OPTIONS = [
  DocumentStatus.CONTEXT,
  DocumentStatus.FRAMING,
  DocumentStatus.DRAFT_TRACKER,
  DocumentStatus.REFRESH,
  DocumentStatus.RETROSPECTIVE,
  DocumentStatus.PLANNING_COMPLETE,
];

export const convertPlanningDocToDocument = (
  editorDoc: EditorDoc | undefined
): TopNavDocument | undefined => {
  if (!editorDoc) {
    return undefined;
  }
  const docStatus = getDocStatus(isHardcodedTemplate(editorDoc.id) ? 'template' : editorDoc.status);

  if (!docStatus) {
    return undefined;
  }
  const defaultStatusOptions =
    editorDoc.template_type === EditorDocTemplateType.NARRATIVE
      ? DEFAULT_NARRATIVE_STATUS_OPTIONS
      : DEFAULT_SWITCHBOARD_STATUS_OPTIONS;
  return {
    id: editorDoc.id,
    title: editorDoc.document?.title,
    name: editorDoc.name || t('untitled'),
    status: docStatus,
    statusOptions: isHardcodedTemplate(editorDoc.id)
      ? [DocumentStatus.TEMPLATE]
      : editorDoc.status_options
        ? editorDoc.status_options.map(getDocStatus)
        : defaultStatusOptions,
    created_at: editorDoc.created_at,
    owner: editorDoc.owner,
  };
};

// Creates a URL for a EditorDoc with slugified title
export const createPlanningDocUrl = (
  doc: {id: string | number; name?: string | null},
  isTemplate?: boolean
): string => {
  const title = doc.name || t('untitled');
  const slug = createSlug(title);

  // Preserve current URL params
  const currentUrl = new URL(window.location.href);
  const searchParams = currentUrl.searchParams.toString();

  // If no title available, just use the ID
  if (!slug) {
    const newUrl = ROUTE_PATHS.PLANNING_DOC.replace(':id', doc.id.toString());
    return searchParams ? `${newUrl}?${searchParams}` : newUrl;
  }

  // Create slug-id format
  const newUrl = ROUTE_PATHS.PLANNING_DOC.replace(
    ':id',
    `${slug}${isTemplate ? '-template' : ''}-${doc.id}`
  );
  return searchParams ? `${newUrl}?${searchParams}` : newUrl;
};

export const getPlanningDocStatus = (status: DocumentStatus): EditorDocStatus => {
  switch (status) {
    case DocumentStatus.DRAFT:
      return EditorDocStatus.DRAFT;
    case DocumentStatus.READY_FOR_REVIEW:
      return EditorDocStatus.REVIEW;
    case DocumentStatus.PUBLISHED:
      return EditorDocStatus.COMPLETE;
    case DocumentStatus.ARCHIVED:
      return EditorDocStatus.ARCHIVE;
    case DocumentStatus.CONTEXT:
      return EditorDocStatus.CONTEXT;
    case DocumentStatus.FRAMING:
      return EditorDocStatus.FRAMING;
    case DocumentStatus.DRAFT_TRACKER:
      return EditorDocStatus.DRAFT_TRACKER;
    case DocumentStatus.PLANNING_COMPLETE:
      return EditorDocStatus.PLANNING_COMPLETE;
    case DocumentStatus.REFRESH:
      return EditorDocStatus.REFRESH;
    case DocumentStatus.RETROSPECTIVE:
      return EditorDocStatus.RETROSPECTIVE;
    default:
      return EditorDocStatus.DRAFT;
  }
};

export const formatCreatedDate = (created_at: string) => formatShortDate(created_at);

export const useActiveThreadIds = (opts?: {
  selector?: string;
  root?: Element | Document | null;
}) => {
  const selector = opts?.selector ?? '[data-highlight-id], [data-comment-id]';
  const root = opts && 'root' in opts ? opts.root : document;
  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  const setOrderedIdsIfChanged = useCallback((nextOrderedIds: string[]) => {
    setOrderedIds((prevOrderedIds) =>
      prevOrderedIds.length === nextOrderedIds.length &&
      prevOrderedIds.every((id, index) => id === nextOrderedIds[index])
        ? prevOrderedIds
        : nextOrderedIds
    );
  }, []);

  const snap = useCallback(() => {
    if (!root) {
      setOrderedIdsIfChanged([]);
      return;
    }

    const els = root.querySelectorAll(selector);
    const seen = new Set<string>();
    const newOrderedIds: string[] = [];
    els.forEach((el) => {
      const e = el as HTMLElement;
      const rawThreadIds = `${e.dataset.highlightId ?? ''} ${e.dataset.commentId ?? ''}`.trim();
      if (!rawThreadIds) return;

      rawThreadIds
        .split(/\s+/)
        .filter(Boolean)
        .forEach((threadId) => {
          if (seen.has(threadId)) return;
          seen.add(threadId);
          newOrderedIds.push(threadId);
        });
    });
    setOrderedIdsIfChanged(newOrderedIds);
  }, [root, selector, setOrderedIdsIfChanged]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!root) {
      setOrderedIdsIfChanged([]);
      return;
    }
    snap();

    let frameId: number | null = null;
    const scheduleSnap = () => {
      if (frameId !== null) return;

      frameId = requestAnimationFrame(() => {
        frameId = null;
        snap();
      });
    };
    const observer = new MutationObserver(() => {
      scheduleSnap();
    });

    const target = root instanceof Document ? root.body : root;
    if (!target) return;

    observer.observe(target, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-highlight-id', 'data-comment-id'],
    });

    return () => {
      observer.disconnect();
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [root, setOrderedIdsIfChanged, snap]);

  useEffect(() => {
    const id = setTimeout(snap, 0);
    return () => clearTimeout(id);
  }, [snap]);

  return orderedIds;
};

export const isThreadAttachedToActiveIds = (
  thread: DocumentThread,
  activeIds: Set<string> | string[]
) => {
  const activeSet = activeIds instanceof Set ? activeIds : new Set(activeIds);
  if (activeSet.has(thread.id)) return true;

  return (
    thread.comments?.some((comment) => !comment.is_deleted && activeSet.has(comment.id)) ?? false
  );
};

export const useFilteredThreads = (
  threads: DocumentThread[] | undefined,
  // Do not pass in activeThreadIds to show all threads even if they aren't attached to DOM
  activeThreadIds: string[] | null = null
) => {
  return useMemo(() => {
    if (!threads) return [];
    return threads.filter((thread) => {
      const hasActive = !thread.is_resolved && thread.comments?.some((c) => !c.is_deleted);
      if (!hasActive) return false;

      // Always return all threads if activeThreadIds is not provided
      if (activeThreadIds === null) return true;

      return isThreadAttachedToActiveIds(thread, activeThreadIds);
    });
  }, [threads, activeThreadIds]);
};

export const getCommentsPanelThreads = (
  threads: DocumentThread[] | undefined,
  filteredActiveThreads: DocumentThread[],
  activeThreadId: string | undefined
) => {
  if (!activeThreadId || filteredActiveThreads.some((thread) => thread.id === activeThreadId)) {
    return filteredActiveThreads;
  }

  // This ensures that resolved thread is available when it is opened (become active comment)
  // through comment history drawer or comment deep link url
  const activeThread = threads?.find((thread) => thread.id === activeThreadId);
  return activeThread ? [...filteredActiveThreads, activeThread] : filteredActiveThreads;
};

export const isHardcodedTemplate = (planningDocId: string) =>
  planningDocId === '14EYgF1u4s1' ||
  planningDocId === 'WJkS1wtbpv' ||
  planningDocId === 'GtoujQRpYWS' ||
  planningDocId === '2LtUpcHRtDW';

export const getMaxNumOfEditorDocs = (planningDocId: string | undefined) => {
  if (planningDocId === 'EYy1YTMvms3') {
    return 6;
  }
  return 5;
};
